"use client";

import { faCheck, faEye, faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "@hookform/error-message";
import { Label } from "radix-ui";
import { type ComponentProps, type ReactNode, forwardRef, useId, useRef, useState } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { FormErrorMessage, Typography, cn } from "../../..";

export interface InputProps extends Omit<ComponentProps<"input">, "ref"> {
	label?: string;
	subLabel?: ReactNode;
	description?: ReactNode;
	innerClassName?: string;
	hideError?: boolean;
	icon?: ReactNode;
	registerOptions?: RegisterOptions<FieldValues>;
	onWarning?: (warning: string) => Promise<string | undefined>;
	preValue?: ReactNode;
	sufValue?: ReactNode | null;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function InputRef(props, ref) {
	const {
		label,
		subLabel,
		description,
		type = "text",
		innerClassName,
		className,
		hideError = false,
		registerOptions,
		onWarning,
		preValue,
		sufValue,
		...remainingProps
	} = props;

	const name = props.name;

	const [warning, setWarning] = useState<string>();

	const id = useId();
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;
	const subLabelId = `${id}-sublabel`;

	const labelRef = useRef<HTMLLabelElement>(null);
	const [hasFocus, setHasFocus] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const methods = useFormContext();
	const registeredProps =
		methods && name
			? methods.register(name, { shouldUnregister: true, ...registerOptions })
			: { ref };

	const invalid = name ? methods?.getFieldState(name)?.invalid : false;
	const hasError = invalid && !hideError;
	const hasWarning = !!warning && !hasError;

	const methodValue: unknown = name ? methods?.getValues(name) : undefined;
	const hasValue = props.value
		? !!props.value
		: Array.isArray(methodValue)
			? !!methodValue.length
			: !!methodValue;
	const isSuccess = !(hasError || hasWarning) && hasValue && !hasFocus;
	const showCheck =
		isSuccess && sufValue !== null && props.type !== "date" && props.type !== "hidden";

	return (
		<div className={cn(className, "relative flex flex-col gap-1")}>
			{!!label && (
				<Label.Root htmlFor={id} ref={labelRef} className="self-start font-bold">
					{label}
				</Label.Root>
			)}

			<div className="relative flex">
				{!!preValue && (
					<div className="pointer-events-none absolute top-0 left-0 flex h-full items-center justify-center gap-1 px-2 text-shades-dark text-sm">
						{preValue}
					</div>
				)}

				<input
					id={id}
					type={type === "password" && showPassword ? "text" : type}
					{...registeredProps}
					{...remainingProps}
					onFocus={(e) => {
						setHasFocus(true);
						remainingProps.onFocus?.(e);
					}}
					onBlur={async (e) => {
						const value = e.target.value;

						if (onWarning && value) {
							setWarning((await methods.trigger(name)) ? await onWarning(value) : undefined);
						}

						setHasFocus(false);
						remainingProps.onBlur?.(e);
					}}
					className={cn(
						"form-input w-full rounded read-only:bg-shades-light/70 focus-visible:ring-inset disabled:cursor-not-allowed disabled:bg-shades-light/70 disabled:opacity-80 motion-safe:transition-colors",
						{
							"border-shades hover:border-primary-medium focus:outline-none focus-visible:border-primary-medium focus-visible:ring-primary-medium":
								!(invalid || warning),
							"border-danger ring-danger focus-visible:border-danger focus-visible:ring-danger":
								invalid,
							"border-warning ring-warning focus-visible:border-warning focus-visible:ring-warning":
								warning && !invalid,
							"border-b-success": isSuccess,
							"pr-[calc(20px+1.5rem)]": showCheck,
						},
						innerClassName,
					)}
					aria-describedby={[
						subLabel ? subLabelId : false,
						description ? descriptionId : false,
						invalid ? errorId : false,
					]
						.filter(Boolean)
						.join(" ")}
				/>

				{sufValue ? (
					<span className="pointer-events-none absolute top-0 right-3.5 flex h-full items-center justify-center">
						{sufValue}
					</span>
				) : props.type === "password" ? (
					<button
						type="button"
						className="absolute top-0 right-3.5 flex h-full appearance-none items-center justify-center enter:text-primary-medium text-dark focus:outline-none motion-safe:transition-colors"
						onClick={(e) => {
							e.preventDefault();
							setShowPassword((prev) => !prev);
						}}
					>
						<FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} fixedWidth size="sm" />
					</button>
				) : (
					showCheck && (
						<span className="pointer-events-none absolute top-0 right-3.5 flex h-full items-center justify-center text-success">
							<FontAwesomeIcon icon={faCheck} fixedWidth />
						</span>
					)
				)}
			</div>

			{!!subLabel && (
				<div
					id={subLabelId}
					className={cn("absolute top-0 right-0 truncate whitespace-nowrap text-right", {
						"text-shades-dark": typeof subLabel === "string",
					})}
					style={{ left: label ? `${label.length + 2}ch` : undefined }}
				>
					{subLabel}
				</div>
			)}

			{!!description && (
				<Typography
					id={descriptionId}
					className="mt-1 text-shades-dark text-sm"
					variant="paragraph"
				>
					{description}
				</Typography>
			)}

			{name && (
				<FormErrorMessage
					variant={hasError ? "danger" : hasWarning ? "warning" : null}
					id={errorId}
				>
					{hasError ? <ErrorMessage name={name} /> : warning}
				</FormErrorMessage>
			)}
		</div>
	);
});
